// Copyright © 2023 The Things Industries B.V.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ErrorMessage } from 'formik'
import { defineMessages } from 'react-intl'
import { useDispatch } from 'react-redux'

import Button from '@ttn-lw/components/button'
import Form, { useFormContext } from '@ttn-lw/components/form'
import Input from '@ttn-lw/components/input'

import Message from '@ttn-lw/lib/components/message'

import OwnersSelect from '@console/containers/owners-select'

import sharedMessages from '@ttn-lw/lib/shared-messages'
import tooltipIds from '@ttn-lw/lib/constants/tooltip-ids'
import attachPromise from '@ttn-lw/lib/store/actions/attach-promise'
import useDebounce from '@ttn-lw/lib/hooks/use-debounce'

import { getGatewayClaimInfoByEui } from '@console/store/actions/gateways'

import GatewayRegistrationFormSection from './gateway-registration-form-section'
import GatewayClaimFormSection from './gateway-claim-form-section'

const m = defineMessages({
  continue: 'To continue, please confirm the Gateway EUI so we can determine onboarding options',
  emtyEui: 'Continue without EUI',
  noEui: 'No gateway EUI',
})

// Save EUI in both fields.
const gatewayEuiEncoder = value => ({
  // Empty strings will be interpreted as zero value (00 fill) by the backend
  // For this reason, they need to be transformed to undefined instead,
  // so that the value will be properly stripped when sent to the backend.
  ids: { eui: value === '' ? undefined : value },
  authenticated_identifiers: {
    gateway_eui: value,
  },
})
const gatewayEuiDecoder = value => (value?.ids?.eui === undefined ? '' : value.ids.eui)
// The default merge of the value setter cannot be used here, since it would
// disregard `undefined` values.
const gatewayEuiValueSetter = ({ setValues }, { value }) =>
  setValues(values => ({
    ...values,
    ids: { ...values.ids, ...value.ids },
    authenticated_identifiers: {
      ...values.authenticated_identifiers,
      ...value.authenticated_identifiers,
    },
  }))

const GatewayProvisioningFormSection = () => {
  const [euiError, setEuiError] = useState(undefined)
  const dispatch = useDispatch()
  const {
    values: {
      _ownerId: ownerId,
      _inputMethod: inputMethod,
      ids: { eui = '' },
    },
    initialValues,
    resetForm,
    setFieldValue,
    setFieldTouched,
    touched,
  } = useFormContext()
  const isEuiMac = useMemo(() => eui?.length === 12, [eui.length])
  const debouncedEui = useDebounce(eui, 3000)
  const isDebouncedEuiMac = useMemo(() => debouncedEui?.length === 12, [debouncedEui])
  const showMacConvert = isEuiMac && (isDebouncedEuiMac || touched.ids?.eui)

  useEffect(() => {
    if (showMacConvert) {
      setFieldTouched('ids.eui', true)
    } else if (isDebouncedEuiMac && !isEuiMac) {
      setFieldTouched('ids.eui', false)
    }
  }, [isDebouncedEuiMac, isEuiMac, setFieldTouched, showMacConvert])

  const hasEmptyEui = eui === ''
  const hasCompletedEui = eui.length === 16
  const hasInputMethod = Boolean(inputMethod)

  // Based on the EUI, we can determine whether the gateway is claimable
  // using the `getInfoByEUI` service. Based on the result, we can
  // toggle claim/manual registration section of the form.
  const handleGatewayEUI = useCallback(async () => {
    setEuiError()

    try {
      if (!hasEmptyEui) {
        const { supports_claiming } = await dispatch(attachPromise(getGatewayClaimInfoByEui(eui)))
        setFieldValue('_inputMethod', supports_claiming ? 'claim' : 'register')

        const prefillId = `eui-${eui.toLowerCase()}`
        if (supports_claiming) {
          setFieldValue('target_gateway_id', prefillId)
        } else {
          setFieldValue('ids.gateway_id', prefillId)
        }
      } else {
        // Gateways without Gateway EUI cannot be claimed.
        setFieldValue('_inputMethod', 'register')
      }
    } catch (error) {
      setEuiError(error)
    }
  }, [dispatch, eui, hasEmptyEui, setFieldValue])

  const handleEuiReset = useCallback(async () => {
    setEuiError()
    resetForm({ values: { ...initialValues, _ownerId: ownerId } })
  }, [initialValues, ownerId, resetForm])

  const handleGatewayEUIKeydown = useCallback(
    event => {
      // Allow using "Enter"-key to confirm the EUI.
      if (hasCompletedEui) {
        if (event.keyCode === 13) {
          handleGatewayEUI()
        }
      }
    },
    [handleGatewayEUI, hasCompletedEui],
  )

  let statusElement = null
  if (euiError) {
    statusElement = <ErrorMessage error={euiError} />
  } else if (!hasInputMethod) {
    statusElement = <Message component="p" content={m.continue} />
  }
  const handleConvertMacToEui = useCallback(() => {
    const euiValue = `${eui.substring(0, 6)}FFFE${eui.substring(6)}`
    setFieldValue('authenticated_identifiers.gateway_eui', euiValue)
    setFieldValue('ids.eui', euiValue)
    setFieldValue('ids.gateway_id', `eui-${euiValue.toLowerCase()}`)
  }, [eui, setFieldValue])

  return (
    <>
      <OwnersSelect name="_ownerId" required />
      <Form.Field
        title={sharedMessages.gatewayEUI}
        name="ids.eui,authenticated_identifiers.gateway_eui"
        type="byte"
        min={8}
        max={8}
        placeholder={hasInputMethod && hasEmptyEui ? m.noEui : sharedMessages.gatewayEUI}
        component={Input}
        tooltipId={tooltipIds.GATEWAY_EUI}
        required={inputMethod === 'claim'}
        disabled={hasInputMethod}
        onKeyDown={handleGatewayEUIKeydown}
        encode={gatewayEuiEncoder}
        decode={gatewayEuiDecoder}
        valueSetter={gatewayEuiValueSetter}
        autoFocus
      >
        {showMacConvert ? (
          <Button
            className="ml-cs-xs"
            type="button"
            message={sharedMessages.convertMacToEui}
            onClick={handleConvertMacToEui}
          />
        ) : hasInputMethod ? (
          <Button
            className="ml-cs-xs"
            type="button"
            message={sharedMessages.reset}
            onClick={handleEuiReset}
          />
        ) : (
          <Button
            className="ml-cs-xs"
            type="button"
            message={hasEmptyEui ? m.emtyEui : sharedMessages.confirm}
            onClick={handleGatewayEUI}
            disabled={!hasCompletedEui && !hasEmptyEui}
          />
        )}
      </Form.Field>
      {inputMethod === 'claim' && <GatewayClaimFormSection />}
      {inputMethod === 'register' && <GatewayRegistrationFormSection />}
      {statusElement}
    </>
  )
}

export { GatewayProvisioningFormSection as default }
